/**
 * @file: ReservationAssociationBanner.tsx
 * @copyright: (c) 2019-2021 sichuan zhichetech co., ltd.
 *
 * Shown at the top of the create-order page when the order is linked to a
 * reservation (picked from the plate's open reservations, or opened from a
 * reservation). A brief read-only summary with a remove control; on order
 * placement the backend converts the linked reservation + its parent lead.
 */

import { Typography } from '@euler/app/components/typography';
import {
  LD,
  cardShadowStyle,
} from '@euler/app/flows/leads/pages/detail/components/leadDetailKit';
import { useOrderFlowContext } from '@euler/app/flows/order/OrderFlowContext';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { useConfirm } from '@euler/functions/ux/useConfirm';
import { Reservation } from '@euler/model/reservation/Reservation';
import { useServiceFactory } from '@euler/services/factory';
import { dateFromValue } from '@euler/utils/datetime';
import format from 'date-fns/format';
import { CalendarClock, X } from 'lucide-react-native';
import { memo, useEffect, useState } from 'react';
import { TouchableOpacity, View } from 'react-native';

const briefOf = (r: Reservation): string => {
  let arrival = '';
  try {
    if (r.expectedArrivalAt) {
      arrival = `${format(
        dateFromValue(r.expectedArrivalAt),
        'MM-dd HH:mm',
      )} 到店`;
    }
  } catch {
    arrival = '';
  }
  return [r.licensePlateNo, arrival, r.customerName]
    .filter(Boolean)
    .join(' · ');
};

export const ReservationAssociationBanner = memo(() => {
  const {
    formController: { form },
  } = useOrderFlowContext();
  const { reservationService } = useServiceFactory();
  const { rpx } = useResponsiveContext();
  const confirm = useConfirm();
  const reservationId = form.reservationId.value;
  const [reservation, setReservation] = useState<Reservation | undefined>(
    undefined,
  );

  const onRemove = async () => {
    const ok = await confirm({
      title: '取消关联预约?',
      message: '取消后，开单时将不再确认此预约。',
      destructive: true,
    });
    if (ok) form.reservationId.update(undefined);
  };

  // Resolve the linked reservation for the summary (the id alone is in the form).
  useEffect(() => {
    let cancelled = false;
    if (!reservationId) {
      setReservation(undefined);
      return;
    }
    void (async () => {
      try {
        const r = await reservationService.get(reservationId);
        if (!cancelled) setReservation(r);
      } catch (e) {
        console.error(e);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [reservationId, reservationService]);

  // Render only once the fetched reservation matches the current link.
  if (!reservationId || reservation?.id !== reservationId) return null;

  return (
    <View
      style={{
        flexDirection: 'row',
        alignItems: 'center',
        backgroundColor: LD.surface,
        marginHorizontal: rpx(12),
        marginTop: rpx(12),
        borderRadius: rpx(12),
        paddingHorizontal: rpx(14),
        paddingVertical: rpx(12),
        ...cardShadowStyle(rpx),
      }}
    >
      <CalendarClock size={rpx(18)} color={LD.brand} />
      <View style={{ flex: 1, marginLeft: rpx(10), paddingRight: rpx(8) }}>
        <Typography.Text size={rpx(13)} color={LD.brand} bold>
          已关联预约
        </Typography.Text>
        <Typography.Text
          size={rpx(12)}
          color={LD.steel}
          mt={rpx(2)}
          numberOfLines={1}
        >
          {briefOf(reservation)}
        </Typography.Text>
      </View>
      <TouchableOpacity
        activeOpacity={0.7}
        onPress={() => void onRemove()}
        hitSlop={{
          top: rpx(10),
          bottom: rpx(10),
          left: rpx(10),
          right: rpx(10),
        }}
      >
        <X size={rpx(18)} color={LD.steel} />
      </TouchableOpacity>
    </View>
  );
});
